import { useApolloClient } from '@apollo/client';
import { Box, Button, Icon } from '@deity/falcon-ui';
import Skeleton from '@material-ui/lab/Skeleton';
import { BaseHelmet, PackageItem, PackagePopout, PackagesHeader, TRow, TableView, Thead } from 'components';
import { APOLLO_BASE_OPTIONS } from 'contracts/constants';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { GetAllPackagesDocument, useGetAllPackagesLazyQuery } from 'services/graphql/generated/hooks';

const Packages: FC = () => {
  const client = useApolloClient();
  const [getAllPackages, { loading: loadingPackages, data: packages, fetchMore }] =
    useGetAllPackagesLazyQuery(APOLLO_BASE_OPTIONS);

  const [recordLimit] = useState(5);

  const onLoadMore = e => {
    e.currentTarget.blur();
    if (fetchMore && packages) {
      fetchMore({
        variables: {
          offset: packages.getAllPackages.data.length,
          limit: recordLimit
        }
      });
    }
  };

  useEffect(() => {
    const cachedPackage = client.readQuery({
      query: GetAllPackagesDocument
    });

    getAllPackages({
      variables: {
        offset: 0,
        limit: cachedPackage ? cachedPackage.getAllPackages.data.length : recordLimit
      }
    });
  }, []);

  const onPackageCreated = () => {
    if (fetchMore && packages) {
      fetchMore({
        variables: {
          offset: 0,
          limit: packages.getAllPackages.data.length + 1
        }
      });
    }
  };

  return (
    <>
      <BaseHelmet page="Packages" />
      <Box>
        <PackagesHeader onPackageCreated={onPackageCreated} />
        <Box
          style={{ border: '1px solid #E0E2E5' }}
          borderRadius="medium"
          pt="lg"
          pb="lg"
          bg="white"
          boxShadow="subtle"
        >
          <TableView gridTemplateColumns="1.2fr 0.6fr 0.8fr 1fr 0.6fr 0.6fr 0.6fr">
            <Thead
              headerItems={[
                { text: 'name' },
                { text: 'last status', cssProps: { justifySelf: 'center' } },
                { text: 'versions', cssProps: { justifySelf: 'center' } },
                { text: 'deployed versions', cssProps: { justifySelf: 'center' } },
                { text: 'modified' },
                { text: 'by' }
              ]}
            />

            {packages &&
              packages.getAllPackages.data.map(p => (
                <TRow key={p.uuid} collapsible content={<PackagePopout {...p} />}>
                  <PackageItem {...p} />
                </TRow>
              ))}
            {loadingPackages && (
              <TRow>
                <>
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                </>
              </TRow>
            )}
            {packages && packages.getAllPackages.data.length < packages.getAllPackages.totalItems && (
              <Button ml="md" mt="xs" onClick={e => onLoadMore(e)} variant="transparent">
                <Icon src="history" stroke="transparent" mr="xs" />
                Older versions
              </Button>
            )}
          </TableView>
        </Box>
      </Box>
    </>
  );
};

export default Packages;
